import { useState } from "react";
import { AGENCY_CONTACT_EMAIL, AGENCY_ID } from "../config/appConfig";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

type SendEmailResult = {
    success: boolean;
    error?: string;
}

interface InfoPropertyMailParams {
    formData: FormData;
    propertyRef: string;
    propertyPrice: number;
    propertyId: number;
    recaptchaToken: string;
}

interface SimpleFormEmailParams {
    data: { [key: string]: any };
    recaptchaToken: string;
}

interface MortgageEmailParams {
    formData: FormData;
    propertyRef: string;
    recaptchaToken: string;
}

export function useSendEmailInfoProperty() {
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const sendEmail = async ({ formData, propertyRef, propertyPrice, propertyId, recaptchaToken }: InfoPropertyMailParams): Promise<SendEmailResult> => {
        setIsLoading(true);
        setEmailError(null);
        setSuccess(false);

        if (!recaptchaToken) {
            const errorMsg = "Errore di verifica reCAPTCHA (token mancante).";
            setEmailError(errorMsg);
            setIsLoading(false);
            return { success: false, error: errorMsg };
        }

        try {
            const data = Object.fromEntries(formData.entries());
            data.info_recipient = AGENCY_CONTACT_EMAIL;
            data.agencyId = String(AGENCY_ID);
            data.info_property_ref = propertyRef;
            data.info_property_price = String(propertyPrice);
            data.info_property_id = String(propertyId);
            data.g_recaptcha_response = recaptchaToken;

            const response = await fetch(`${API_BASE_URL}/send-email-info-property`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                let errorMsg = `Errore HTTP ${response.status}`;
                try {
                    const errorResult = await response.json();
                    errorMsg = errorResult.error || errorMsg;
                } catch (jsonError) { /* Ignora */ }
                throw new Error(errorMsg);
            }

            const result = await response.json();

            if (result.success) {
                setSuccess(true);
                return { success: true };
            } else {
                const errorMsg = result.error || "Errore sconosciuto dal backend.";
                setEmailError(errorMsg);
                return { success: false, error: errorMsg };
            }
        } catch (err) {
            const message = (err instanceof Error) ? err.message : "Si è verificato un errore nella richiesta.";
            console.error("Errore durante l'invio dell'email:", err);
            setEmailError(message);
            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    };

    return { sendEmail, isLoading, emailError, success };
}

export function useSendEmailSell() {
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const sendEmail = async ({ data: inputData, recaptchaToken }: SimpleFormEmailParams): Promise<SendEmailResult> => {
        setIsLoading(true);
        setEmailError(null);
        setSuccess(false);

        if (!recaptchaToken) {
            const errorMsg = "Errore di verifica reCAPTCHA (token mancante).";
            setEmailError(errorMsg);
            setIsLoading(false);
            return { success: false, error: errorMsg };
        }

        try {
            const dataToSend = { ...inputData };
            dataToSend.sell_recipient = AGENCY_CONTACT_EMAIL;
            dataToSend.agencyId = String(AGENCY_ID);
            dataToSend.g_recaptcha_response = recaptchaToken;

            const response = await fetch(`${API_BASE_URL}/send-email-sell`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                let errorMsg = `Errore HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorResult = await response.json();
                    errorMsg = errorResult.error || errorMsg;
                } catch (jsonError) { /* Ignora */ }
                throw new Error(errorMsg);
            }

            const result = await response.json();
            if (result.success) {
                setSuccess(true);
                return { success: true };
            } else {
                const errorMsg = result.error || "Errore sconosciuto durante l'invio della proposta.";
                setEmailError(errorMsg);
                return { success: false, error: errorMsg };
            }
        } catch (err) {
            const message = (err instanceof Error) ? err.message : "Si è verificato un errore nella richiesta.";
            console.error("Errore durante l'invio della proposta:", err);
            setEmailError(message);
            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    };

    return { sendEmail, isLoading, emailError, success };
}

export function useSendEmailBuy() {
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const sendEmail = async ({ data: inputData, recaptchaToken }: SimpleFormEmailParams): Promise<SendEmailResult> => {
        setIsLoading(true);
        setEmailError(null);
        setSuccess(false);

        if (!recaptchaToken) {
            const errorMsg = "Errore di verifica reCAPTCHA (token mancante).";
            setEmailError(errorMsg);
            setIsLoading(false);
            return { success: false, error: errorMsg };
        }

        try {
            const dataToSend = { ...inputData };
            dataToSend.buy_recipient = AGENCY_CONTACT_EMAIL;
            dataToSend.agencyId = String(AGENCY_ID);
            dataToSend.g_recaptcha_response = recaptchaToken;

            const response = await fetch(`${API_BASE_URL}/send-email-buy`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataToSend),
            });

            if (!response.ok) {
                let errorMsg = `Errore HTTP ${response.status}`;
                try {
                    const errorResult = await response.json();
                    errorMsg = errorResult.error || errorResult.message || errorMsg;
                } catch (jsonError) { /* Ignora */ }
                throw new Error(errorMsg);
            }

            const result = await response.json();
            if (result.success) {
                setSuccess(true);
                return { success: true };
            } else {
                const errorMsg = result.error || "Errore sconosciuto durante l'invio della richiesta.";
                setEmailError(errorMsg);
                return { success: false, error: errorMsg };
            }
        } catch (err) {
            const message = (err instanceof Error) ? err.message : "Si è verificato un errore nella richiesta.";
            console.error("useSendEmailBuy: Errore durante l'invio:", err);
            setEmailError(message);
            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    };

    return { sendEmail, isLoading, emailError, success };
}

export function useSendEmailMortgage() {
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const sendEmail = async ({ formData, propertyRef, recaptchaToken }: MortgageEmailParams): Promise<SendEmailResult> => {
        setIsLoading(true);
        setEmailError(null);
        setSuccess(false);

        if (!recaptchaToken) {
            const errorMsg = "Errore di verifica reCAPTCHA (token mancante).";
            setEmailError(errorMsg);
            setIsLoading(false);
            return { success: false, error: errorMsg };
        }

        try {
            // Converti FormData in un oggetto semplice
            const data = Object.fromEntries(formData.entries());

            // Aggiungi i dati necessari per il backend
            data.mortgage_recipient = AGENCY_CONTACT_EMAIL;
            data.agencyId = String(AGENCY_ID);
            data.property_ref = propertyRef; // Riferimento immobile
            data.g_recaptcha_response = recaptchaToken;

            const response = await fetch(`${API_BASE_URL}/send-email-mortgage`, { // <-- NUOVO ENDPOINT
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                let errorMsg = `Errore HTTP ${response.status}`;
                try {
                    const errorResult = await response.json();
                    errorMsg = errorResult.error || errorMsg;
                } catch (jsonError) { console.error(jsonError) }
                throw new Error(errorMsg);
            }

            const result = await response.json();

            if (result.success) {
                setSuccess(true);
                return { success: true };
            } else {
                const errorMsg = result.error || "Errore sconosciuto dal backend.";
                setEmailError(errorMsg);
                return { success: false, error: errorMsg };
            }
        } catch (err) {
            const message = (err instanceof Error) ? err.message : "Si è verificato un errore nella richiesta.";
            console.error("Errore durante l'invio dell'email di richiesta mutuo:", err);
            setEmailError(message);
            return { success: false, error: message };
        } finally {
            setIsLoading(false);
        }
    };

    // Resetta lo stato di successo per permettere nuove sottomissioni (utile se il modale non si chiude subito)
    const resetSuccessState = () => setSuccess(false);

    return { sendEmail, isLoading, emailError, success, resetSuccessState };
}

// in src/hooks/useSendEmail.ts
// ...

export function useSendEmailValuation() {
    const [isLoading, setIsLoading] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const sendEmail = async (data: { [key: string]: any }, recaptchaToken: string) => {
        setIsLoading(true);
        setEmailError(null);
        setSuccess(false);

        try {
            const dataToSend = {
                ...data,
                agencyId: String(AGENCY_ID),
                g_recaptcha_response: recaptchaToken
            };

            const response = await fetch(`${API_BASE_URL}/send-email-valuation`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dataToSend),
            });

            const result = await response.json();
            if (result.success) {
                setSuccess(true);
                return { success: true };
            } else {
                throw new Error(result.error || "Errore sconosciuto");
            }
        } catch (err: any) {
            setEmailError(err.message);
            return { success: false, error: err.message };
        } finally {
            setIsLoading(false);
        }
    };

    const resetSuccessState = () => setSuccess(false);

    return { sendEmail, isLoading, emailError, success, resetSuccessState };
}